import * as FadeLegacyParser from '../../serialization/FadeLegacyParser';
import { Element } from '../Element';

/**
 * ImageElement class representing an image element in the timeline
 */
export class ImageElement extends Element {
  source: string; // Creatomate uses 'source' for media URL
  fit?: string; // Creatomate property for how the image fits its container

  constructor(props: Partial<ImageElement> = {}) {
    super({ ...props, type: 'image' });
    // Support both source and src properties for backwards compatibility
    this.source = props.source ?? (props as any).src ?? '';
    this.fit = props.fit ?? 'contain';
    // Note: opacity, x_scale, y_scale, y_alignment are now handled by Element constructor
  }

  /**
   * Updates the image element's properties
   * @param props Properties to update
   */
  override update(props: Partial<ImageElement>): void {
    super.update(props);

    // Handle image-specific properties
    if (props.source !== undefined) {
      this.source = props.source as string;
    }
    if (props.fit !== undefined) {
      this.fit = props.fit as string;
    }
    // Note: opacity, x_scale, y_scale, y_alignment are now handled by Element.update()
  }

  /**
   * Captures the mutable state of the image element.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, any> {
    const state = {
      ...super.getState(),
      source: this.source,
      fit: this.fit,
    };

    return state;
  }

  /**
   * Restores the image element's mutable state from a state record.
   * @param state The state to restore.
   */
  override restoreState(state: Record<string, any>): void {
    super.restoreState(state);
    if (state.source !== undefined) this.source = state.source;
    if (state.fit !== undefined) this.fit = state.fit;
    // Note: opacity, x_scale, y_scale, y_alignment are now handled by Element.restoreState()
  }

  protected override getAdditionalProperties(): Record<string, any> {
    const properties: Record<string, any> = {
      source: this.source,
      fit: this.fit,
    };

    return properties;
  }

  /**
   * Creates a deep clone of this image element
   * Override the base Element.clone() to ensure we create an ImageElement, not a generic Element
   */
  override clone(): ImageElement {
    const el = new ImageElement({
      ...this.getState(),
      // Ensure specific image properties are copied
      source: this.source,
      fit: this.fit,
    });

    return el;
  }

  /**
   * Creates an ImageElement from JSON data
   */
  static fromJSON(json: Record<string, any>): ImageElement {
    // Parse time-based properties that Element would usually parse
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    // Parse legacy fade overlays into native animation types
    let animations = Array.isArray(json.animations) ? json.animations : [];
    let colorOverlay = json.color_overlay ?? null;
    const legacyFade = FadeLegacyParser.parseFromOverlays(colorOverlay);
    if (legacyFade) {
      animations = [
        ...animations.filter(
          (a: any) => a.type !== 'dip' && a.type !== 'crossfade',
        ),
        ...FadeLegacyParser.toNativeAnimations(legacyFade),
      ];
      colorOverlay = null;
    }

    return new ImageElement({
      // Base element properties
      id: json.id,
      name: json.name ?? null,
      track: json.track ?? null,
      time: timeValue,
      duration: parsedDuration,
      type: 'image', // Force image type
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: json.x ?? '50%',
      y: json.y ?? '50%',
      width: json.width ?? '100%',
      height: json.height ?? '100%',

      // Transform properties (now handled by Element)
      aspect_ratio: json.aspect_ratio ?? null,
      x_padding: json.x_padding ?? '0 vw',
      y_padding: json.y_padding ?? '0 vh',
      z_index: json.z_index ?? null,
      x_anchor: json.x_anchor ?? '50%',
      y_anchor: json.y_anchor ?? '50%',
      x_scale: json.x_scale ?? '100%',
      y_scale: json.y_scale ?? '100%',
      x_skew: json.x_skew ?? '0',
      y_skew: json.y_skew ?? '0',
      x_rotation: json.x_rotation ?? '0',
      y_rotation: json.y_rotation ?? '0',
      z_rotation: json.z_rotation ?? '0',
      perspective: json.perspective ?? null,
      backface_visible: json.backface_visible ?? true,
      border_radius: json.border_radius ?? '0 vmin',

      // Alignment properties (now handled by Element)
      x_alignment: json.x_alignment ?? '50%',
      y_alignment: json.y_alignment ?? '50%',

      // Visual effects (now handled by Element)
      opacity: json.opacity ?? '100%',
      blend_mode: json.blend_mode ?? 'none',
      clip: json.clip ?? false,

      // Animation support (now handled by Element)
      animations,
      color_overlay: colorOverlay,

      // Image-specific properties
      source: json.source ?? json.src ?? '',
      fit: json.fit ?? 'cover',
    });
  }
}
